import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import {
  removeContentFromMetadata,
  trackDashboardEvent,
} from '@modules/dashboard/helpers';
import { useUpsertDashboard } from '@modules/dashboard/hooks';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { DashboardState } from '@modules/dashboard/store/types';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { DashboardCell } from 'generated/graphql';

interface RemoveTextWidgetDialogContentProps {
  cell: DashboardCell;
  metadata: DashboardState['metadata'];
}

export const RemoveTextWidgetDialogContent = ({
  cell,
  metadata,
}: RemoveTextWidgetDialogContentProps) => {
  const { upsertDashboard, loading } = useUpsertDashboard();
  const dispatch = useDashboardStore(({ dispatch }) => dispatch);
  const { closeModal } = useMuiModalContext();
  const onClose = () => closeModal();

  const removeContent = async () => {
    const metadataClone = removeContentFromMetadata({
      metadata,
      contentID: cell.contentID,
    });

    dispatch({
      type: DashboardActionType.SET_METADATA,
      payload: metadataClone,
    });

    await upsertDashboard({
      dashboardProps: {
        metadataV2: metadataClone,
        deleteWidget: {
          id: cell.contentID,
        },
      },
      shouldRefetchDashboard: true,
    });

    trackDashboardEvent({
      eventName: 'Dashboard Remove Content',
      type: 'text',
      from: 'Dashboard Page',
    });

    closeModal();
  };

  return (
    <>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h6">Remove from Dashboard?</Typography>
          <IconButton aria-label="close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Typography variant="body1">
          Are you sure you want to remove this from the dashboard?
        </Typography>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Stack direction="row" m={1} spacing={2}>
          <Button onClick={onClose} variant="outlined">
            No, keep it
          </Button>
          <LoadingButton
            loading={loading}
            onClick={removeContent}
            variant="contained"
            color="error"
            autoFocus
          >
            Remove
          </LoadingButton>
        </Stack>
      </DialogActions>
    </>
  );
};
